import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';

/**
 * Backup dos anexos — RF-ANE-04.
 *
 * ================================================================
 * POR QUE É UM SEGUNDO PACOTE, E NÃO UMA CAIXA NO PRIMEIRO
 * ================================================================
 *
 * O backup do banco é um dump SQL — não inclui arquivo nenhum, e nunca precisou
 * incluir, porque até agora tudo que existia estava no banco.
 *
 * Com anexos isso mudou: restaurar só o banco devolve as fichas dos documentos
 * apontando para arquivos que não existem mais. A tela de lançamento passa a
 * mostrar "arquivo ausente" — e é por isso que aquele aviso existe lá.
 *
 * Juntar os dois num pacote só mexeria em gerar, verificar, restaurar, importar
 * e podar um backup que já funciona. Dois arquivos custam lembrar de guardar os
 * dois, e não arriscam o que está de pé.
 */

type Pacote = {
  file: string;
  size_bytes: number;
  created_at: string;
  files: number | null;
  reason: string | null;
};

function tamanho(bytes: number): string {
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1).replace('.', ',')} MB`;
  return `${(bytes / 1024).toFixed(1).replace('.', ',')} KB`;
}

function momento(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

export function AttachmentBackupSection() {
  const queryClient = useQueryClient();
  const [aviso, setAviso] = useState<string | null>(null);

  const lista = useQuery({
    queryKey: ['attachment-backups'],
    queryFn: () => api.get<{ data: Pacote[] }>('/backups/attachments'),
    select: (r) => r.data,
  });

  const atualizar = () => queryClient.invalidateQueries({ queryKey: ['attachment-backups'] });

  const gerar = useMutation({
    mutationFn: () => api.post<{ data: Pacote }>('/backups/attachments'),
    onSuccess: async (r) => {
      setAviso(`Pacote gerado com ${r.data.files} arquivo(s).`);
      await atualizar();
    },
    onError: (e) => setAviso(e instanceof ApiError ? e.message : 'Não foi possível gerar.'),
  });

  const restaurar = useMutation({
    mutationFn: (file: string) =>
      api.post<{ message: string }>(`/backups/attachments/${file}/restore`),
    onSuccess: (r) => setAviso(r.message),
    onError: (e) => setAviso(e instanceof ApiError ? e.message : 'Não foi possível restaurar.'),
  });

  const excluir = useMutation({
    mutationFn: (file: string) => api.delete(`/backups/attachments/${file}`),
    onSuccess: atualizar,
  });

  const pacotes = lista.data ?? [];

  return (
    <div className="mt-8 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-semibold">Backup dos anexos</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Diário às 03:30, logo depois do backup do banco. Os documentos não entram no dump
            SQL — este pacote é o que os traz de volta.
          </p>
        </div>

        <button
          type="button"
          onClick={() => gerar.mutate()}
          disabled={gerar.isPending}
          className="rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-60"
        >
          {gerar.isPending ? 'Gerando…' : 'Gerar agora'}
        </button>
      </div>

      {aviso && (
        <p
          role="status"
          className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-900 dark:border-indigo-900/50 dark:bg-indigo-950/30 dark:text-indigo-200"
        >
          {aviso}
        </p>
      )}

      {pacotes.length === 0 && !lista.isPending && (
        <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
          Nenhum pacote ainda. Ele é gerado sozinho quando houver ao menos um documento anexado.
        </p>
      )}

      <ul className="mt-3 divide-y divide-slate-100 dark:divide-slate-800">
        {pacotes.map((p) => (
          <li key={p.file} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5 text-sm">
            <div className="min-w-0 flex-1">
              <p className="font-medium">{momento(p.created_at)}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {p.files ?? '?'} arquivo(s) · {tamanho(p.size_bytes)}
                {p.reason === 'agendado' && ' · automático'}
              </p>
            </div>

            <button
              type="button"
              onClick={() => void api.download(`/backups/attachments/${p.file}/download`)}
              className="text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400"
            >
              Baixar
            </button>

            <button
              type="button"
              disabled={restaurar.isPending}
              onClick={() => {
                /**
                 * Restaurar ACRESCENTA — por isso a confirmação é leve.
                 *
                 * Diferente da restauração do banco, aqui nada é apagado: o que
                 * já está no disco fica, e só volta o que estiver faltando.
                 */
                if (
                  window.confirm(
                    'Restaurar os documentos deste pacote?\n\n' +
                      'Nada é apagado: os anexos que já estão no servidor continuam como estão, ' +
                      'e só voltam os que estiverem faltando.',
                  )
                ) {
                  restaurar.mutate(p.file);
                }
              }}
              className="text-xs font-medium text-indigo-600 hover:underline disabled:opacity-50 dark:text-indigo-400"
            >
              Restaurar
            </button>

            <button
              type="button"
              disabled={excluir.isPending}
              onClick={() => {
                if (window.confirm(`Excluir o pacote de ${momento(p.created_at)}?`)) {
                  excluir.mutate(p.file);
                }
              }}
              className="text-xs font-medium text-rose-600 hover:underline disabled:opacity-50 dark:text-rose-400"
            >
              Excluir
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
